import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { actions as reviewsActions } from 'src/redux/reviews/slice';
import { requestProductReviews, requestReviewsPage } from 'src/redux/reviews/thunks';
import type { AppThunkDispatch } from 'src/redux/store';

/**
 * Fetches the reviews data the page cannot render on the server, once per product.
 *
 * Keyed on the product code rather than on mount: a client-side navigation between two
 * PDPs reuses the page component, so an effect that ran once would leave the second
 * product with the first one's reviews. The slice is reset first so nothing from the
 * previous product shows while the new fetch is in flight, and its in-flight requests go
 * stale. An effect runs after the render that first shows the new product, so the
 * selectors also refuse fields fetched for another product code. Client-side only because
 * Feefo's cold responses are far too slow for getServerSideProps.
 */
export const useProductReviewsFetch = (productCode: number | undefined) => {
  const dispatch = useDispatch() as AppThunkDispatch;

  useEffect(() => {
    if (!productCode) {
      return;
    }

    dispatch(reviewsActions.resetForProduct());
    dispatch(requestProductReviews({ autoInitiated: true, page: 1, productCode }));
    // The first Feefo page also carries the rating breakdown and partner insights.
    dispatch(requestReviewsPage({ autoInitiated: true, page: 1, productCode }));
  }, [dispatch, productCode]);
};
